import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Chip,
  Collapse,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import {
  Close as CloseIcon,
  ExpandMore as ExpandIcon,
  Groups as GroupsIcon,
  Lock as LockIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import SessionDetailDrawer from 'components/session/SessionDetailDrawer';
import PremiumCard from 'components/shared/surfaces/PremiumCard';
import StatCard from 'components/gamification/StatCard';
import UserAvatar from 'components/shared/user/UserAvatar';
import LoadingBlock from 'components/shared/blocks/LoadingBlock';
import requestStatus from 'common/constants/status';
import { toneOf } from 'components/admin-organizations/wellbeingTones';
import { ORGANIZATION_DEPARTMENT_DETAIL } from 'api/graphql/wellbeing';

const EASE = 'cubic-bezier(0.32, 0.72, 0, 1)';

const DONUT_SIZE = 132;
const DONUT_STROKE = 14;
const DONUT_RADIUS = (DONUT_SIZE - DONUT_STROKE) / 2;
const DONUT_CIRC = 2 * Math.PI * DONUT_RADIUS;

// A partir de esta cantidad de personas la lista deja de ser navegable de un vistazo.
const SEARCH_THRESHOLD = 5;
const PAGE_SIZE = 12;

function LegendRow({ color, label, value }) {
  return (
    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
        <Box sx={{ width: 9, height: 9, borderRadius: '50%', backgroundColor: color, flex: 'none' }} />
        <Typography variant="caption" sx={{ color: 'text.secondary' }} noWrap>
          {label}
        </Typography>
      </Stack>
      <Typography variant="caption" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
        {value}
      </Typography>
    </Stack>
  );
}
LegendRow.propTypes = {
  color: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.number.isRequired
};

/**
 * Donut de adopción con la misma receta SVG del donut de asistencia (dos arcos sobre un anillo
 * base, `rotate(-90)`, sin librerías). Es un componente propio y no el de sesiones porque aquel
 * trae copy de agenda ("Completadas / Programadas / Ausencias") que aquí mentiría.
 */
function AdoptionDonut({
  percent = 0,
  activeMembers,
  inactiveMembers,
  activeLabel,
  inactiveLabel,
  centerLabel
}) {
  const value = Math.max(0, Math.min(100, percent || 0));
  const dash = (value / 100) * DONUT_CIRC;

  return (
    <Stack direction="row" spacing={2} alignItems="center">
      <Box sx={{ position: 'relative', width: DONUT_SIZE, height: DONUT_SIZE, flex: 'none' }}>
        <Box component="svg" viewBox={`0 0 ${DONUT_SIZE} ${DONUT_SIZE}`} sx={{ width: '100%', height: '100%' }}>
          <Box
            component="circle"
            cx={DONUT_SIZE / 2}
            cy={DONUT_SIZE / 2}
            r={DONUT_RADIUS}
            fill="none"
            strokeWidth={DONUT_STROKE}
            sx={{ stroke: 'currentColor', color: 'hairline.strong' }}
          />
          <Box
            component="circle"
            cx={DONUT_SIZE / 2}
            cy={DONUT_SIZE / 2}
            r={DONUT_RADIUS}
            fill="none"
            strokeWidth={DONUT_STROKE}
            strokeLinecap="round"
            strokeDasharray={`${dash} ${DONUT_CIRC - dash}`}
            transform={`rotate(-90 ${DONUT_SIZE / 2} ${DONUT_SIZE / 2})`}
            sx={{ stroke: 'currentColor', color: toneOf(value), transition: `stroke-dasharray 700ms ${EASE}` }}
          />
        </Box>
        <Stack alignItems="center" justifyContent="center" sx={{ position: 'absolute', inset: 0 }}>
          <Typography variant="h5" component="div" sx={{ fontWeight: 800, lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
            {Math.round(value)}%
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {centerLabel}
          </Typography>
        </Stack>
      </Box>
      <Stack spacing={1} sx={{ flexGrow: 1, minWidth: 0 }}>
        <LegendRow color={toneOf(value)} label={activeLabel} value={activeMembers} />
        <LegendRow color="hairline.strong" label={inactiveLabel} value={inactiveMembers} />
      </Stack>
    </Stack>
  );
}
AdoptionDonut.propTypes = {
  percent: PropTypes.number,
  activeMembers: PropTypes.number.isRequired,
  inactiveMembers: PropTypes.number.isRequired,
  activeLabel: PropTypes.string.isRequired,
  inactiveLabel: PropTypes.string.isRequired,
  centerLabel: PropTypes.string.isRequired
};
// Barra de porcentaje: misma receta `scaleX` + `toneOf` del tablero de bienestar.
function MetricBar({ label, value = null }) {
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Typography variant="caption" fontWeight={700} sx={{ width: 118, flex: 'none' }}>
        {label}
      </Typography>
      <Box sx={{ flex: 1, height: 6, borderRadius: 999, backgroundColor: 'surface.overlay', overflow: 'hidden' }}>
        <Box
          sx={{
            height: '100%',
            width: '100%',
            borderRadius: 999,
            backgroundColor: toneOf(value),
            transform: `scaleX(${(value || 0) / 100})`,
            transformOrigin: 'left',
            transition: `transform 600ms ${EASE}`
          }}
        />
      </Box>
      <Typography
        variant="caption"
        fontWeight={800}
        sx={{ width: 40, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: toneOf(value) }}>
        {value === null || value === undefined ? '—' : `${Math.round(value)}%`}
      </Typography>
    </Stack>
  );
}
MetricBar.propTypes = { label: PropTypes.string.isRequired, value: PropTypes.number };
function MiniStat({ label, value }) {
  return (
    <Grid item xs={6}>
      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>
        {value}
      </Typography>
    </Grid>
  );
}
MiniStat.propTypes = { label: PropTypes.string.isRequired, value: PropTypes.node.isRequired };

/** Fila-card de persona: colapsada muestra identidad y estado, expandida su uso completo. */
function PersonRow({ person, lastSeenLabel, t }) {
  const [expanded, setExpanded] = useState(false);
  const toggle = () => setExpanded((prev) => !prev);

  return (
    <Box
      role="button"
      tabIndex={0}
      aria-expanded={expanded}
      onClick={toggle}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          toggle();
        }
      }}
      sx={{
        p: 1.5,
        borderRadius: '14px',
        border: '1px solid',
        borderColor: 'hairline.main',
        backgroundColor: 'surface.content',
        cursor: 'pointer',
        transition: `transform 200ms ${EASE}, border-color 200ms ${EASE}`,
        '&:hover': { borderColor: 'hairline.strong' },
        '&:active': { transform: 'scale(0.98)' },
        '&:focus-visible': { outline: '2px solid var(--otx-focus-ring)', outlineOffset: 2 }
      }}>
      <Stack direction="row" spacing={1.25} alignItems="center">
        <UserAvatar size={34} textSize={13} user={person} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
              {person.full_name}
            </Typography>
            <Chip
              size="small"
              label={person.is_active
                ? t('memberActive', { ns: 'organizations', defaultValue: 'Activo' })
                : t('memberInactive', { ns: 'organizations', defaultValue: 'Inactivo' })}
              sx={{
                flex: 'none',
                height: 19,
                fontSize: '0.62rem',
                fontWeight: 700,
                backgroundColor: person.is_active ? 'sessionStatus.completed.bg' : 'surface.overlay',
                color: person.is_active ? 'sessionStatus.completed.fg' : 'text.secondary'
              }}
            />
          </Stack>
          <Typography variant="caption" sx={{ color: 'text.secondary' }} noWrap>
            {person.plan_name || t('noPlan', { ns: 'organizations', defaultValue: 'Sin plan activo' })} · {lastSeenLabel}
          </Typography>
        </Box>
        <ExpandIcon
          sx={{
            flex: 'none',
            fontSize: 20,
            color: 'text.secondary',
            transform: expanded ? 'rotate(180deg)' : 'none',
            transition: `transform 200ms ${EASE}`
          }}
        />
      </Stack>

      <Collapse in={expanded} unmountOnExit>
        <Divider sx={{ my: 1.5 }} />
        <Grid container spacing={1.25}>
          <MiniStat label={t('workoutsTile', { ns: 'organizations', defaultValue: 'Entrenamientos' })} value={person.workouts} />
          <MiniStat label={t('minutesTile', { ns: 'organizations', defaultValue: 'Minutos' })} value={person.minutes} />
          <MiniStat
            label={t('completionLabel', { ns: 'organizations', defaultValue: 'Cumplimiento' })}
            value={person.avg_completion_percent === null || person.avg_completion_percent === undefined
              ? '—'
              : `${person.avg_completion_percent}%`}
          />
          <MiniStat label={t('recoveryLabel', { ns: 'organizations', defaultValue: 'Recuperación' })} value={person.recovery_sessions} />
          <MiniStat label={t('resultsLabel', { ns: 'organizations', defaultValue: 'Resultados' })} value={person.results} />
          <MiniStat label={t('lastSeenLabel', { ns: 'organizations', defaultValue: 'Último acceso' })} value={lastSeenLabel} />
        </Grid>
      </Collapse>
    </Box>
  );
}
PersonRow.propTypes = {
  person: PropTypes.object.isRequired,
  lastSeenLabel: PropTypes.string.isRequired,
  t: PropTypes.func.isRequired
};

/**
 * Bienestar Corporativo (admin/RRHH) — panel derecho de un departamento: cuánto USA la app su
 * gente. Donut de adopción, tiles agregados, barras porcentuales y la lista de personas.
 *
 * PRIVACIDAD: aquí solo entra actividad (entrenamientos, minutos, último acceso). El score de
 * bienestar de una persona y quién respondió qué NO se muestran jamás; el índice del área llega
 * ya agregado y con k-anonymity resuelta en el backend (`wellbeing.protected` → candado).
 */
function DepartmentDetailDrawer({
  open,
  onClose,
  organizationId,
  department = null,
  weeks = 4,
  wellbeing = null
}) {
  const { t } = useTranslation(['organizations']);
  const [search, setSearch] = useState('');
  const [visible, setVisible] = useState(PAGE_SIZE);

  const { data, loading, error, refetch } = useQuery(ORGANIZATION_DEPARTMENT_DETAIL, {
    variables: {
      organization_id: organizationId,
      department_id: department?.id ?? null,
      weeks
    },
    skip: !open,
    fetchPolicy: 'cache-and-network'
  });

  const detail = data?.organizationDepartmentDetail || null;
  const people = useMemo(() => detail?.people || [], [detail]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return people;
    return people.filter((person) => person.full_name?.toLowerCase().includes(term));
  }, [people, search]);

  // "Nunca / Hoy / Ayer / Hace N días / fecha": el dato crudo (ISO) no le dice nada a RRHH.
  const lastSeenLabelOf = (iso) => {
    if (!iso) return t('lastSeenNever', { defaultValue: 'Sin ingresos' });
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return t('lastSeenNever', { defaultValue: 'Sin ingresos' });
    const days = Math.floor((Date.now() - date.getTime()) / 86400000);
    if (days <= 0) return t('lastSeenToday', { defaultValue: 'Hoy' });
    if (days === 1) return t('lastSeenYesterday', { defaultValue: 'Ayer' });
    if (days < 30) return t('lastSeenDays', { n: days, defaultValue: 'Hace {{n}} días' });
    return date.toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const handleClose = () => {
    setSearch('');
    setVisible(PAGE_SIZE);
    onClose();
  };

  const departmentName = department?.name || t('wellbeingNoDepartment', { defaultValue: 'Sin departamento' });
  const inactiveMembers = detail ? Math.max(0, detail.members - detail.active_members) : 0;

  return (
    <SessionDetailDrawer open={open} onClose={handleClose}>
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1} sx={{ mb: 0.5 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h6" component="h2" sx={{ fontWeight: 800 }} noWrap>
            {departmentName}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {t('adoptionWindow', { n: weeks, defaultValue: 'Últimas {{n}} semanas' })}
          </Typography>
        </Box>
        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flex: 'none' }}>
          {detail && (
            <Chip
              size="small"
              label={t('departmentMembersN', { n: detail.members, defaultValue: '{{n}} miembros' })}
              sx={{
                height: 22,
                fontSize: '0.68rem',
                fontWeight: 700,
                backgroundColor: 'surface.overlay',
                border: '1px solid',
                borderColor: 'hairline.main'
              }}
            />
          )}
          <IconButton
            size="small"
            onClick={handleClose}
            aria-label={t('closeDetail', { defaultValue: 'Cerrar detalle' })}
            sx={{ color: 'text.secondary' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Stack>

      <Stack direction="row" spacing={0.75} alignItems="flex-start" sx={{ mb: 2.5 }}>
        <LockIcon sx={{ fontSize: 14, color: 'text.disabled', mt: '2px', flex: 'none' }} />
        <Typography variant="caption" sx={{ color: 'text.disabled' }}>
          {t('adoptionPrivacyNote', {
            defaultValue: 'Solo se muestra el uso de la app. El bienestar de cada persona nunca se revela.'
          })}
        </Typography>
      </Stack>

      {loading && !detail && (
        <LoadingBlock
          status={requestStatus.loading}
          isValid={false}
          type="table"
          skeletonProps={{ rows: 5, height: 72 }}
        />
      )}

      {error && !detail && (
        <Stack
          spacing={1.5}
          alignItems="flex-start"
          sx={{ p: 2, borderRadius: '14px', border: '1px solid', borderColor: 'hairline.main', backgroundColor: 'surface.content' }}>
          <Typography variant="body2" sx={{ fontWeight: 700, color: 'sessionStatus.absent.fg' }}>
            {t('departmentDetailError', { defaultValue: 'No pudimos cargar el detalle de este departamento.' })}
          </Typography>
          <Button
            size="small"
            onClick={() => refetch()}
            sx={{ textTransform: 'none', fontWeight: 700, color: 'gold.main', '&:active': { transform: 'scale(0.98)' } }}>
            {t('retry', { defaultValue: 'Reintentar' })}
          </Button>
        </Stack>
      )}

      {detail && (
        <Stack spacing={2.5}>
          <PremiumCard padding={2.5}>
            <AdoptionDonut
              percent={detail.adoption_percent}
              activeMembers={detail.active_members}
              inactiveMembers={inactiveMembers}
              centerLabel={t('adoption', { defaultValue: 'Adopción' })}
              activeLabel={t('activeMembers30', { defaultValue: 'Activos (30 días)' })}
              inactiveLabel={t('withoutActivity', { defaultValue: 'Sin actividad' })}
            />
          </PremiumCard>

          <Grid container spacing={1.5}>
            <Grid item xs={6}>
              <StatCard title={t('membersTile', { defaultValue: 'Miembros' })} value={detail.members} />
            </Grid>
            <Grid item xs={6}>
              <StatCard title={t('activeTile', { defaultValue: 'Activos' })} value={detail.active_members} />
            </Grid>
            <Grid item xs={6}>
              <StatCard title={t('workoutsTile', { defaultValue: 'Entrenamientos' })} value={detail.workouts} />
            </Grid>
            <Grid item xs={6}>
              <StatCard title={t('minutesTile', { defaultValue: 'Minutos' })} value={detail.minutes} />
            </Grid>
            <Grid item xs={12}>
              <StatCard
                title={t('avgCompletionTile', { defaultValue: 'Cumplimiento promedio' })}
                value={detail.avg_completion_percent === null ? '—' : `${detail.avg_completion_percent}%`}
                subtitle={t('workoutsPerMember', {
                  n: detail.workouts_per_member,
                  defaultValue: '{{n}} entrenamientos por persona'
                })}
              />
            </Grid>
          </Grid>

          <PremiumCard padding={2.5}>
            <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1.5 }}>
              {t('adoptionRates', { defaultValue: 'Ritmo de uso' })}
            </Typography>
            <Stack spacing={1.25}>
              <MetricBar label={t('adoptionMonth', { defaultValue: 'Activos 30 días' })} value={detail.adoption_percent} />
              <MetricBar label={t('adoptionWeek', { defaultValue: 'Activos 7 días' })} value={detail.weekly_adoption_percent} />
              <MetricBar label={t('completionLabel', { defaultValue: 'Cumplimiento' })} value={detail.avg_completion_percent} />
            </Stack>
          </PremiumCard>

          {wellbeing && (
            <PremiumCard padding={2.5}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="subtitle2" fontWeight={800}>
                    {t('wellbeingOfArea', { defaultValue: 'Índice de bienestar del área' })}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {wellbeing.protected
                      ? t('wellbeingProtected', {
                          defaultValue: 'Protegido por privacidad: se necesitan al menos 5 personas respondiendo.'
                        })
                      : t('wellbeingAggregateNote', { defaultValue: 'Promedio anónimo del área, nunca por persona.' })}
                  </Typography>
                </Box>
                {wellbeing.protected ? (
                  <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flex: 'none' }}>
                    <LockIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                    <Typography variant="caption" sx={{ color: 'text.disabled', fontWeight: 700 }}>
                      {t('wellbeingProtectedShort', { defaultValue: 'Protegido' })}
                    </Typography>
                  </Stack>
                ) : (
                  <Typography
                    variant="h5"
                    component="div"
                    sx={{ fontWeight: 800, flex: 'none', fontVariantNumeric: 'tabular-nums', color: toneOf(wellbeing.score) }}>
                    {wellbeing.score ?? '—'}
                  </Typography>
                )}
              </Stack>
            </PremiumCard>
          )}

          <Box>
            <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1.5 }}>
              {t('peopleTitle', { defaultValue: 'Personas' })}
            </Typography>

            {people.length > SEARCH_THRESHOLD && (
              <TextField
                fullWidth
                size="small"
                placeholder={t('searchPerson', { defaultValue: 'Buscar persona' })}
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setVisible(PAGE_SIZE);
                }}
                sx={{ mb: 1.5 }}
                InputProps={{
                  // El buscador no lleva label visible: el nombre accesible va en el input nativo.
                  inputProps: { 'aria-label': t('searchPerson', { defaultValue: 'Buscar persona' }) },
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 18 }} />
                    </InputAdornment>
                  )
                }}
              />
            )}

            {people.length === 0 && (
              <Stack
                spacing={1}
                alignItems="center"
                sx={{ py: 5, px: 2, textAlign: 'center', border: '1px dashed', borderColor: 'hairline.main', borderRadius: '14px' }}>
                <GroupsIcon sx={{ fontSize: 34, color: 'text.disabled' }} />
                <Typography variant="body2" fontWeight={700}>
                  {t('departmentNoPeople', { defaultValue: 'Este departamento aún no tiene miembros.' })}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {t('departmentNoPeopleHelp', {
                    defaultValue: 'Asígnalos desde la pestaña Miembros para hacerle seguimiento a su uso de la app.'
                  })}
                </Typography>
              </Stack>
            )}

            {people.length > 0 && filtered.length === 0 && (
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {t('noPersonMatch', { defaultValue: 'Ninguna persona coincide con la búsqueda.' })}
              </Typography>
            )}

            <Stack spacing={1}>
              {filtered.slice(0, visible).map((person) => (
                <PersonRow
                  key={person.user_id}
                  person={person}
                  lastSeenLabel={lastSeenLabelOf(person.last_seen_at)}
                  t={t}
                />
              ))}
            </Stack>

            {filtered.length > visible && (
              <Button
                fullWidth
                onClick={() => setVisible((prev) => prev + PAGE_SIZE)}
                sx={{
                  mt: 1.5,
                  textTransform: 'none',
                  fontWeight: 700,
                  color: 'gold.main',
                  '&:active': { transform: 'scale(0.98)' }
                }}>
                {t('showMoreN', { n: filtered.length - visible, defaultValue: 'Ver {{n}} más' })}
              </Button>
            )}
          </Box>
        </Stack>
      )}
    </SessionDetailDrawer>
  );
}

DepartmentDetailDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  organizationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  // `id` null = personas de la organización sin departamento asignado.
  department: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string
  }),
  weeks: PropTypes.number,
  // Agregado de bienestar del área (ya con k-anonymity resuelta), opcional.
  wellbeing: PropTypes.shape({
    score: PropTypes.number,
    protected: PropTypes.bool
  })
};

export default DepartmentDetailDrawer;
